import React from 'react';
import { TableRow, TableWrapper, Td, Th, THead, Table } from '@increasecard/typed-components';
import { Operations } from '@card/types';
import PaymentMethodLabel from '../../../common/components/PaymentMethodLabel';
import Paginator from '../../../common/components/Paginator';
import { useTranslation } from 'react-i18next';
import { useLocale, Currency } from '@increasecard/increase-core';
import { CountryFilter, formatDate, LoadingOverlay } from '@card/commonlib';

interface OperationsListProps {
  currentPage: number;
  fetchingData: boolean;
  selectPage: (page: number) => void;
  operations: Operations[];
  totalPages: number;
}

const renderCard = (operation: Operations) => {
  if (operation.card) {
    return (
      <PaymentMethodLabel
        cardBrand={operation.card_type}
        cardNumber={operation.card.slice(operation.card.length - 4)}
      />
    );
  }
  if (operation.card_type) {
    return operation.card_type.toUpperCase();
  }
  return '-';
};

const OperationsList = ({
  fetchingData,
  operations,
  totalPages,
  currentPage,
  selectPage
}: OperationsListProps) => {
  const { t } = useTranslation();
  const locale = useLocale();
  if (locale === null) return null;
  const hasData = operations && operations.length > 0;
  return (
    <>
      <LoadingOverlay active={fetchingData} style={{ top: '1px', left: '-1px' }}>
        <TableWrapper>
          <Table hasData={hasData} zebra>
            <THead>
              <tr>
                <Th>{t('operations:List:Headers:date')}</Th>
                <Th>{t('operations:List:Headers:payDate')}</Th>
                <Th>{t('operations:List:Headers:card')}</Th>
                <Th>{t('operations:List:Headers:establishmentNumber')}</Th>
                <Th>{t('operations:List:Headers:shopName')}</Th>
                <Th>{t('operations:List:Headers:voucher')}</Th>
                <Th>{t('operations:List:Headers:batchNumber')}</Th>
                <Th>{t('operations:List:Headers:description')}</Th>
                <CountryFilter hideFor={['dominicana']}>
                  <Th>{t('operations:List:Headers:installment')}</Th>
                </CountryFilter>
                <Th rightAligned>{t('operations:List:Headers:amountCents')}</Th>
              </tr>
            </THead>
            <tbody>
              {operations.map((operation, index) => (
                <TableRow key={index}>
                  <Td>{formatDate(operation.date, locale)}</Td>
                  <Td>{formatDate(operation.pay_date, locale)}</Td>
                  <Td>{renderCard(operation)}</Td>
                  <Td>{operation.stablishment_number}</Td>
                  <Td>{operation.shop_name}</Td>
                  <Td>{operation.voucher}</Td>
                  <Td>{operation.batch_number}</Td>
                  <Td>{operation.description || '-'}</Td>
                  <CountryFilter hideFor={['dominicana']}>
                    <Td>{operation.installment}</Td>
                  </CountryFilter>
                  <Td rightAligned>
                    <Currency value={Number(operation.amount_cents) / 100} />
                  </Td>
                </TableRow>
              ))}
            </tbody>
          </Table>
          {operations.length > 0 && (
            <Paginator
              align="right"
              onSelectPage={selectPage}
              pages={totalPages}
              selected={currentPage}
            />
          )}
        </TableWrapper>
      </LoadingOverlay>
    </>
  );
};

export default OperationsList;
